import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import CancelButton from "../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import QueryButton from "../../../../components/QueryButton";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import { toast } from "../../../../components/toastNotification";
import {
  selectedModule,
  selectedReports,
} from "../../../CarrierAgreementMgmt/ReportsDynamic/DynamicReports/atoms";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import {
  addCustomReport,
  editCustomReport,
  rowForEditCustomReport,
  selectedColumn,
  runCustomQuery,
  isRunCustomQuery,
  isQueryClicked,
  addNewReport,
} from "../atom/atoms";
import useAddCustomReports from "../hooks/useAddCustomReports";
import useEditCustomReport from "../hooks/useEditCustomReport";
import { useGetActiveRoles } from "../hooks/useGetActiveRoles";
import { ICustomReportDetails, IPermission } from "../types";
import CustomReportDetails from "./CustomReportDetails";
import CustomReportQueryBuilder from "./CustomReportQueryBuilder";

interface CustomReportFormProps {}

const CustomReportForm: React.FC<CustomReportFormProps> = ({}) => {
  const [isAddState, setIsAddState] = useRecoilState(addCustomReport);
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditCustomReport);

  const [isEditState, setIsEditState] = useRecoilState(editCustomReport);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newData, setNewData] = useState<ICustomReportDetails>();
  const selectedColumns = useRecoilValue<any>(selectedColumn);
  const [isRunQuery, setIsRunQuery] = useRecoilState(isRunCustomQuery);
  const [customQuery, setCustomQuery] = useRecoilState(runCustomQuery);
  const [queryClicked, setQueryClicked] = useRecoilState(isQueryClicked);
  const [isAddNewReport, setIsAddNewReport] = useRecoilState(addNewReport);
  const [isShowResult, setIsShowResult] = useState(false);
  const selectedReport = useRecoilValue(selectedReports);
  const moduleName = useRecoilValue(selectedModule);
  const user = useRecoilValue(accountAtom);
  const queryClient = useQueryClient();
  const history = useHistory();

  const useFormMethods = useForm<ICustomReportDetails>({
    defaultValues: { ...rowForEdit },
  });

  const {
    handleSubmit,
    formState: { isDirty },
    watch,
  } = useFormMethods;
  const {
    mutateAsync: addCustomReports,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    isLoading,
    reset: resetAddData,
  } = useAddCustomReports();

  const {
    mutateAsync: updateCustomReports,
    isSuccess: updateSuccess,
    isError: updateError,
    data: updateResult,
    isLoading: updateIsLoading,
    reset: resetUpdateData,
  } = useEditCustomReport();
  const { data: Roles, isLoading: isRolesLoading } = useGetActiveRoles();
  const RolesOpt =
    Roles &&
    Roles?.map((x) => {
      return {
        label: x.roleName,
        value: x.roleId,
      };
    });
  const onSubmit = (data: ICustomReportDetails) => {
    const permission = validateRoles(data?.permissionIds);
    const hasChange = hasFieldChange(data);
    if (hasChange) {
      if (isDirty === true) {
        const moduleName = data?.moduleName?.toLowerCase()?.replace(/\s/g, "");
        const reportName = data?.reportName?.replace(/\s/g, "");
        const reportPath = `/reportstab/${moduleName}/${reportName}`;
        setNewData({
          ...data,
          path: reportPath,
          moduleName: data?.moduleName,
          customFields: data?.customFields,
          permissionIds: permission ?? [],
        });
        setIsModalOpen(true);
      } else {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    } else {
      toast.fire("Saving Failed", "No Changes have been made", "warning");
    }
  };
  const validateRoles = (permissionIds: IPermission[] | null) => {
    if (!permissionIds) {
      const permission = Roles?.filter((c) => c.roleId === user?.roleId)?.map(
        (x) => {
          return {
            roleName: x.roleName,
            roleId: x.roleId,
            isActive: x.isActive,
          };
        }
      );
      return permission;
    }
    const permission = permissionIds?.map((c) => {
      return {
        id: c.id,
        roleId: c.value,
        roleName: c.label,
        isActive: true,
      };
    });
    return permission;
  };
  const hasFieldChange = (data: ICustomReportDetails) => {
    let isSame = false;
    if (data?.customFields && data?.customFields?.length > 0) {
      const newFieldData = data?.customFields?.map((x) => {
        return {
          columnName: x.columnName,
          logic: x.logic,
          operator: x.operator,
          value: x.value,
        };
      });
      const oldFieldData: any = rowForEdit?.customFields?.map((x) => {
        return {
          columnName: x.columnName,
          logic: x.logic,
          operator: x.operator,
          value: x.value,
        };
      });
      isSame = compareArrays(newFieldData, oldFieldData);
    }
    if (isSame === false) {
      return true;
    }

    if (isSame === true && isDirty === true) {
      return true;
    }

    return false;
  };
  function compareArrays(arr1, arr2) {
    try {
      // If lengths are different, arrays are definitely different
      if (arr1?.length !== arr2?.length) {
        return false;
      }

      // Iterate over each object in the arrays
      for (let i = 0; i < arr1?.length; i++) {
        const obj1 = arr1[i];
        const obj2 = arr2[i];

        // Compare each property of the objects
        for (let key in obj1) {
          if (obj1[key] !== obj2[key]) {
            return false; // If any property is different, arrays are different
          }
        }
      }
      // If all objects are the same, arrays are the same
      return true;
    } catch (error) {
      return false;
    }
  }
  const handleRunQuery = () => {
    setIsRunQuery(false);
    const columns =
      selectedColumns?.length > 0
        ? selectedColumns?.map((obj: any) => obj.value)?.join(",")
        : "";
    const field: any = watch("customFields")
      ? watch("customFields")?.map((x) => {
          return {
            ...x,
          };
        })
      : [];

    setCustomQuery({
      ...customQuery,
      tableColumns: columns,
      condition: field,
      moduleName: watch("moduleName"),
      orderBy: watch("orderBy"),
    });

    setIsShowResult(true);
    setQueryClicked(true);
  };

  const handleCancel = () => {
    if (isAddNewReport) {
      setIsAddNewReport(false);
      const url = {
        pathname:
          "/reportstab/" +
          moduleName?.replace(/\s+/g, "")?.toLowerCase() +
          "/" +
          selectedReport?.replace(/\s+/g, ""),
      };
      history.push(url);
      window.location.reload();
    } else {
      setCustomQuery({
        label: "",
        value: "",
        tableColumns: "",
        condition: [],
        script: "",
        moduleName: "",
      });
      setIsAddState(false);
      setIsEditState(false);
      setIsRunQuery(false);
      setQueryClicked(false);
      setRowForEdit(undefined);
    }
  };
  const handleSave = () => {
    setIsModalOpen(false);
    if (isAddState) {
      addCustomReports(newData);
    } else {
      updateCustomReports(newData);
    }
  };

  if (addSuccess && addResult) {
    if (addResult.data === "duplicate") {
      toast.fire("Failed", "Report Name Already Exists.", "error");
      setIsModalOpen(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      queryClient.invalidateQueries("GetCustomReports");
      handleCancel();
      toast.fire("Success", "Report Added Successfully", "success");
    } else if (addResult.status === 500) {
      toast.fire(
        "Server Error",
        "Failed to Add Reports! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetCustomReports");
      setIsAddState(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Reports! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetCustomReports");
      setIsAddState(false);
    }
    resetAddData();
    setIsModalOpen(false);
  }
  if (addError) {
    setIsModalOpen(false);
    toast.fire("Server Error", "Failed to Add Report! No Data Saved", "error");
    queryClient.invalidateQueries("GetCustomReports");
    setIsAddState(false);
  }
  if (updateSuccess && updateResult) {
    if (updateResult.data === "duplicate") {
      toast.fire("Failed", "Report Name Already Exists.", "error");
      setIsModalOpen(false);
    } else if (updateResult.status === 201 || updateResult.status === 200) {
      queryClient.invalidateQueries("GetCustomReports");
      handleCancel();
      toast.fire("Success", "Report Updated Successfully", "success");
    } else if (updateResult.status === 500) {
      toast.fire(
        "Server Error",
        "Failed to Update Reports! No Data Saved",
        "error"
      );
    } else {
      toast.fire(
        "Server Error",
        "Failed to Update Reports! No Data Saved",
        "error"
      );
    }
    resetUpdateData();
    setIsModalOpen(false);
  }
  if (updateError) {
    setIsModalOpen(false);
    toast.fire(
      "Server Error",
      "Failed to Update Report! No Data Saved",
      "error"
    );
  }

  useEffect(() => {
    if (isShowResult) {
      setIsRunQuery(true);
      setIsShowResult(false);
    }
  }, [isShowResult]);

  return (
    <>
      <div className="relative">
        <div className="divide-y">
          <div className="px-4 pb-4 pt-4 border-l-2 border-primary  box-shadow ">
            <FormProvider {...useFormMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-2">
                  <div className="ml-2 font-bold text-primary text-md">
                    {isAddState ? "Add Report Details" : "Edit Report Details"}
                  </div>
                  <div className="flex justify-between gap-2 mr-2">
                    <SubmitHookForm
                      type="submit"
                      disabled={isLoading}
                      buttonText={isLoading ? "Saving.." : "Save"}
                      className={undefined}
                      onClick={undefined}
                    />
                    <CancelButton
                      onClick={() => {
                        handleCancel();
                      }}
                    />
                  </div>
                </div>
                <div className="border-b-2 border-secondary mr-2 ml-2"></div>
              </form>
              <div className="flex justify-between my-3">
                <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
                  <b> Note:</b> All fields with * are required to fill.
                </div>
                <div>
                  <div className="flex flex-row items-center gap-2 px-2">
                    <QueryButton
                      buttonText="Run Query"
                      disabled={false}
                      onClick={(e) => {
                        e.preventDefault();
                        handleRunQuery();
                      }}
                    />
                  </div>
                </div>
              </div>
              <div>
                <CustomReportDetails rowForEdit={rowForEdit} />
              </div>
              {isRunQuery && (
                <div className="mt-4 w-full">
                  <div className="grid w-full items-center gap-2">
                    <CustomReportQueryBuilder />
                  </div>
                </div>
              )}
            </FormProvider>
          </div>
          {isModalOpen && (
            <ConfirmBoxModal
              ModalTitle={
                isAddState ? "Add Report Details" : "Update Report Details"
              }
              qConfirmation={`Are you sure you want to ${
                isAddState ? "Add " : "Update "
              } this ?`}
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
              }}
              onClickFunction={() => {
                handleSave();
              }}
              cancelButtonRef={undefined}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CustomReportForm;
